import React from 'react';
import { TextField, Autocomplete, Box, InputAdornment, Typography } from '@mui/material';
import { Phone as PhoneIcon } from '@mui/icons-material';
import { Country, COUNTRIES } from '../../data/countries';

interface PhoneInputProps {
   phoneNumber: string;
   selectedCountry: Country;
   onPhoneNumberChange: (phoneNumber: string) => void;
   onCountryChange: (country: Country) => void;
   label?: string;
   placeholder?: string;
   error?: boolean;
   helperText?: string | null;
   disabled?: boolean;
   required?: boolean;
   fullWidth?: boolean;
   onKeyPress?: (e: React.KeyboardEvent) => void;
}

const PhoneInput: React.FC<PhoneInputProps> = ({
   phoneNumber,
   selectedCountry,
   onPhoneNumberChange,
   onCountryChange,
   label = 'Phone Number',
   placeholder = 'Enter phone number',
   error = false,
   helperText,
   disabled = false,
   required = false,
   fullWidth = true,
   onKeyPress,
}) => {
   return (
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
         <Autocomplete
            options={COUNTRIES}
            getOptionLabel={(option) => option.flag}
            value={selectedCountry}
            onChange={(event, newValue) => {
               if (newValue) {
                  onCountryChange(newValue);
               }
            }}
            renderInput={(params) => (
               <TextField
                  {...params}
                  label='Country'
                  variant='outlined'
                  disabled={disabled}
                  sx={{
                     minWidth: 100,
                     width: 100,
                     '& .MuiInputBase-root': {
                        height: '56px', // Match standard TextField height
                     },
                  }}
                  InputProps={{
                     ...params.InputProps,
                     style: { fontSize: '1.2rem', textAlign: 'center' }, // Make flag larger and centered
                  }}
               />
            )}
            renderOption={(props, option) => (
               <Box
                  component='li'
                  {...props}
                  sx={{
                     display: 'flex',
                     alignItems: 'center',
                     gap: 1.5,
                     px: 2,
                     py: 1.5,
                     minHeight: '48px',
                     '&:hover': {
                        backgroundColor: 'action.hover',
                     },
                  }}
               >
                  <Box sx={{ fontSize: '1.2rem', minWidth: '28px', textAlign: 'center' }}>
                     {option.flag}
                  </Box>
                  <Box
                     sx={{
                        flex: 1,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                     }}
                  >
                     <Typography variant='body2' sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                        {option.name}
                     </Typography>
                     <Typography
                        variant='body2'
                        sx={{ color: 'text.secondary', fontSize: '0.875rem', ml: 1 }}
                     >
                        {option.dialCode}
                     </Typography>
                  </Box>
               </Box>
            )}
            sx={{
               width: 100,
               minWidth: 100,
               flexShrink: 0,
            }}
            disabled={disabled}
            componentsProps={{
               popper: {
                  sx: {
                     '& .MuiAutocomplete-listbox': {
                        minWidth: '320px',
                        maxHeight: '300px',
                     },
                     '& .MuiPaper-root': {
                        minWidth: '320px',
                     },
                  },
               },
            }}
         />
         <TextField
            fullWidth
            label={label}
            value={phoneNumber}
            onChange={(e) => onPhoneNumberChange(e.target.value)}
            onKeyPress={onKeyPress}
            disabled={disabled}
            placeholder={placeholder}
            error={error}
            helperText={helperText || undefined}
            required={required}
            sx={{ flex: 1 }}
            InputProps={{
               startAdornment: (
                  <InputAdornment position='start'>
                     <PhoneIcon color='primary' />
                  </InputAdornment>
               ),
            }}
         />
      </Box>
   );
};

export default PhoneInput;
